<template>
  <div :id="id" :class="$style.marketingSet" data-marketing-set-v="3.2.0">
    <button
      :class="[$style.titleBar, computedIsOdd && $style.titleBarOdd]"
      :style="{
        backgroundImage: data?.fields?.titleBarBackground
          ? `url('${data?.fields?.titleBarBackground}?h=94&w=1310&fit=fill&q=70')`
          : undefined,
      }"
      @click="assetsVisible = !assetsVisible"
    >
      <BasePicture
        v-if="data?.fields?.logo?.fields?.file?.url"
        :src="`${data?.fields?.logo?.fields?.file?.url}?h=74&q=70`"
        :img-class="$style.logo"
        :class="$style.picture"
        :alt="data?.fields?.name || ''"
        aria-hidden="true"
      />
      <div :class="$style.name">{{ data?.fields?.name }}</div>
      <div :class="$style.toggle">
        <IconChevronUp v-if="assetsVisible" />
        <IconChevronDown v-else />
      </div>
    </button>
    <div
      v-if="assetsVisible && computedAssets?.length"
      :class="$style.marketingAssets"
    >
      <MarketingAsset
        v-for="asset in computedAssets"
        :key="asset?.sys?.id"
        :data="asset"
        :set-name="data?.fields?.name"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TypeMarketingMaterialSetWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { $visibility, $userAccessCheck } = useNuxtApp()
const { highestUserLevel } = storeToRefs(useUserStore())

// Props
const props = defineProps<{
  id?: string
  data: TypeMarketingMaterialSetWithoutUnresolvableLinksResponse
  place: number
  searchQuery: string
}>()

// Data
const assetsVisible = ref(
  props.id ? window?.location?.hash?.substring(1) === props.id : false,
)

// Computed
const computedAssets = computed(() => {
  if (!props?.data?.fields?.assets) return []

  let assets = props.data.fields.assets.filter((a) => {
    const isVisible = $visibility(a?.fields?.visibility)
    const hasAccess = $userAccessCheck(
      a?.fields?.accessLevel,
      highestUserLevel.value,
    )
    return isVisible && hasAccess
  })

  if (props.searchQuery) {
    assets = assets.filter((asset) =>
      asset?.fields?.title
        ?.toLowerCase()
        .includes(props.searchQuery.toLowerCase?.()),
    )
  }

  return assets || []
})

const computedIsOdd = computed(() => props?.place % 2)

// Methods
const onHashChange = () => {
  assetsVisible.value = props.id
    ? window?.location?.hash?.substring(1) === props.id
    : false
}

onMounted(() => {
  window.addEventListener('hashchange', onHashChange)
})
</script>

<style lang="scss" module>
.marketingSet {
  scroll-margin-top: 60px;
}

.titleBar {
  align-items: center;
  background-color: var(--gray-8);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border: 1px solid var(--gray-7);
  color: var(--white);
  cursor: pointer;
  display: flex;
  min-height: 94px;
  height: min-content;
  justify-content: flex-start;
  overflow: hidden;
  padding: 10px;
  width: 100%;
  font-size: inherit;
  font-family: inherit;

  @include media('sm') {
    height: 94px;
  }
}

.titleBarOdd {
  background-color: var(--gray-7);
}

.picture {
  align-items: center;
  display: flex;
  flex: 0 0 72px;
  height: 100%;
  max-width: 72px;
  max-height: 72px;
  text-align: center;

  @include media('sm') {
    flex: 0 0 200px;
    justify-content: center;
    max-width: 200px;
  }
}

.logo {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.name {
  font-weight: 700;
  flex: 1 1 auto;
  margin-left: 1.5em;

  @include media('sm') {
    flex: 0 0 auto;
  }
}

.toggle {
  align-items: center;
  background-color: var(--purple);
  border-radius: 50%;
  color: var(--white);
  display: flex;
  flex: 0 0 26px;
  height: 26px;
  justify-content: center;
  margin-left: 1.5em;
  width: 26px;

  svg {
    width: 11px;
    height: auto;
  }
}

.marketingAssets {
  display: grid;
  grid-gap: 20px;
  margin: 20px;
  padding-bottom: 60px;
  grid-template-columns: 1fr;

  @include media('sm') {
    grid-gap: 20px;
    grid-template-columns: repeat(3, minmax(0, 300px));
    justify-content: center;
    margin: 20px;
  }
}
</style>
